import { useCallback, useMemo, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import { scrollToId } from 'lib/window/scrollUtils'
import { CONTENT_SECTION_IDS } from './NavigableOfferContent/constants'
import { NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET } from './NavigableOfferContent/useNavigableOfferContentScroll'

const locationMarkerIcon = <LineMapMarkerIcon />
const mapIcon = <LineMapIcon />

const LocationHeadingLink = styled(TextLink)`
  color: ${props => props.theme.palette.neutral.default.two};

  &.has-icon-start > :first-child {
    margin-inline-end: ${rem(4)};
  }

  &:is(a, button):hover {
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

interface Props {
  location?: string
  searchUrl?: string
  testId?: string
  variant?: 'small' | 'medium'
  showMapLink?: boolean
  onViewMapClick?: () => void
}

function OfferLocationHeading(props: Props) {
  const {
    location,
    searchUrl,
    testId = 'offer-location',
    variant = 'small',
    showMapLink = false,
    onViewMapClick,
  } = props

  const intl = useIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const hasLocation = !!location

  const handleViewMapClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (onViewMapClick) {
      onViewMapClick()
      return
    }

    scrollToId(CONTENT_SECTION_IDS.location, -NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET)
  }, [onViewMapClick])

  const renderMapLink = useCallback((chunks: ReactNode) => (
    <LocationHeadingLink
      startIcon={mapIcon}
      size={variant}
      weight="regular"
      onClick={handleViewMapClick}
    >
      {chunks}
    </LocationHeadingLink>
  ), [handleViewMapClick, variant])

  const locationContent = useMemo(() => (
    <LocationHeadingLink
      to={searchUrl}
      startIcon={locationMarkerIcon}
      size={variant}
      weight="regular"
      underline={!!searchUrl}
    >
      {location}
    </LocationHeadingLink>
  ), [location, searchUrl, variant])

  const locationAndMapMessage = useMemo(() => (
    <FormattedMessage
      id="_MbgVMNOtK5xVwB1sakK"
      defaultMessage="{location} {sep} <link>View on map</link>"
      
      values={{
        location: locationContent,
        sep,
        link: renderMapLink,
      }}
    />
  ), [locationContent, renderMapLink, sep])

  const mapOnlyMessage = useMemo(() => (
    <FormattedMessage
      id="fjeFzLeG9LtWfS4Ma1OT"
      defaultMessage="<link>View on map</link>"
      
      values={{
        link: renderMapLink,
      }}
    />
  ), [renderMapLink])

  if (!hasLocation && !showMapLink) {
    return null
  } else if (hasLocation && showMapLink) {
    return (
      <BodyTextBlock data-testid={testId} variant={variant}>
        {locationAndMapMessage}
      </BodyTextBlock>
    )
  } else if (!hasLocation && showMapLink) {
    return (
      <BodyTextBlock data-testid={testId} variant={variant}>
        {mapOnlyMessage}
      </BodyTextBlock>
    )
  }
  return (
    <BodyTextBlock data-testid={testId} variant={variant}>
      {locationContent}
    </BodyTextBlock>
  )
}

export default OfferLocationHeading
